<template>
  <div>
    <organism-credential
      v-if="filterData"
      :filter-list="filterData"
      :currentVeeamId="currentServerVeeam.id"
      :fields="fields"
      :items="credentialList"
      :serverCredentialList="serverCredentialList"
      :veeamList="veeamList"
      @onUpdateFilterData="onUpdateFilterData"
      @onUpdateCurrentPage="onUpdateCurrentPage"
      @onSetDefaultFilterData="onSetDefaultFilterData"
      @createCredential="createCredentialToApi"
      @editCredential="editCredentialToApi"
      @deleteCredential="deleteCredentialToApi"
    ></organism-credential>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import {
  getVeeamList,
  getVeeamCredential,
  createVeeamCredential,
  editVeeamCredential,
  changePasswordVeeamCredential,
  deleteVeeamCredential,
  getVeeamServer,
} from "@/api/api-module3"
import mixins from "@/mixins"
import mixinsValidator from "@/mixins/mixinsValidator"
import { mapActions, mapGetters } from "vuex"
import OrganismCredential from "@/components/organisms/backupManagement/OrganismCredential.vue"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"
export default {
  name: "Credential",
  components: { OrganismCredential },
  mixins: [mixins, mixinsValidator, mixinsObjectHelper],
  data() {
    return {
      veeam: "",
      fields: [
        {
          key: "username",
          label: "USERNAME",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "type",
          label: "TYPE",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "description",
          label: "DESCRIPTION",
          thClass: "w-250px",
          tdClass: "w-250px",
        },
        {
          key: "creationTime",
          label: "CREATION TIME",
          thClass: "w-200px",
          tdClass: "w-200px",
        },
        {
          key: "deleteIcon",
          label: "",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
      ],
      credentialList: [],
      serverCredentialList: [],
      veeamList: [],
      filterData: null,
    }
  },
  computed: {
    ...mapGetters("app", ["getCurrentServerVeeam"]),
    ...mapGetters("appFilter", ["getBMMCredentialFilter"]),

    currentServerVeeam() {
      return this.getCurrentServerVeeam
    },
  },
  watch: {
    currentServerVeeam() {
      if (this.hasString(this.currentServerVeeam.id)) {
        this.getServerCredentialListFromApi()
        this.getDataFromApi()
      }
    },
  },
  async mounted() {
    this.setShowVeeam(true)
    await this.getVeeamListFromApi()
    if (this.hasString(this.currentServerVeeam.id)) {
      await this.getServerCredentialListFromApi()
      await this.getDataFromApi()
    }
  },
  beforeDestroy() {
    this.setShowVeeam(false)
  },
  methods: {
    ...mapActions("app", ["setShowVeeam"]),
    ...mapActions("appFilter", ["setBMMCredentialFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getBMMCredentialFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    async getDataFromApi() {
      await this.getCredentialListFromApi()
    },

    async getVeeamListFromApi() {
      this.cloneFilterDataFromVuex()
      this.showLoading(true)
      this.veeamList = []
      const baseFilter = {}
      await getVeeamList(baseFilter)
        .then((response) => {
          response.data.forEach((veeam) => {
            this.veeamList.push({
              id: veeam.veeamId,
              name: veeam.veeamName,
              ip: veeam.veeamAddress,
              port: veeam.veeamPort,
              username: veeam.veeamUsername,
              description: veeam.veeamDescription,
            })
          })
        })
        .finally(() => this.showLoading(false))
    },

    async getCredentialListFromApi() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = this.filterDecoration()
      await getVeeamCredential(filterData)
        .then((response) => {
          if (response) {
            this.filterData.totalCount = response.pagination.total
            const updatedFilter = {
              ...this.getBMMCredentialFilter,
              totalCount: response.pagination.total,
            }
            this.setBMMCredentialFilter(updatedFilter)
            this.credentialList = response.data.map((credential) => {
              return {
                id: credential.id,
                username: credential.username,
                type: credential.type,
                description: credential.description,
                creationTime: {
                  date: DateTime.fromISO(credential.creationTime).toFormat(
                    "dd/MM/yyyy"
                  ),
                  time: DateTime.fromISO(credential.creationTime).toFormat(
                    "HH:mm:ss"
                  ),
                },
                autoElevated: credential.autoElevated,
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    filterDecoration() {
      const filter = {
        veeamId: this.currentServerVeeam.id,
        NameFilter: this.filterData.username || undefined,
        Page: this.filterData.currentPage,
        PageSize: 10,
      }
      return filter
    },

    // click filter
    onUpdateFilterData(filter) {
      filter.currentPage = 1
      this.setBMMCredentialFilter(filter)
      this.getCredentialListFromApi()
    },

    onUpdateCurrentPage(filter) {
      const updatedFilter = {
        ...this.getBMMCredentialFilter,
        currentPage: filter.currentPage,
      }
      this.setBMMCredentialFilter(updatedFilter)
      this.getCredentialListFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        username: "",
        description: "",
        currentPage: 1,
        totalCount: 0,
      }
      this.setBMMCredentialFilter(defaultFilter)
      this.getCredentialListFromApi()
    },

    async createCredentialToApi(createCredential, veeam) {
      this.showLoading(true)
      veeam.forEach(async (element) => {
        await createVeeamCredential(createCredential, element.id)
          .then(async (create) => {
            if (create.status === "ok") {
              this.toastSuccess("Created successfully")
              await this.getDataFromApi()
            } else {
              this.toastError("Created failed")
            }
          })
          .catch(() => {
            this.toastError("Created failed")
          })
          .finally(() => this.showLoading(false))
      })
    },
    async editCredentialToApi(id, editCredential, editPassword) {
      // change password
      if (this.hasString(editPassword.password)) {
        this.showLoading(true)
        await changePasswordVeeamCredential(
          id,
          editPassword,
          this.currentServerVeeam.id
        )
          .then((changePassword) => {
            if (changePassword.status == "ok") {
              this.toastSuccess("Change password successfully")
            } else {
              this.toastError("Change password failed")
            }
          })
          .catch(() => {
            this.toastError("Change password failed")
          })
          .finally(() => this.showLoading(false))
      }
      // change credential information
      await editVeeamCredential(id, editCredential, this.currentServerVeeam.id)
        .then(async (editCredential) => {
          if (editCredential.status === "ok") {
            this.toastSuccess("Edit successfully")
            await this.getDataFromApi()
          } else {
            this.toastError("Edit failed")
          }
        })
        .catch(() => {
          this.toastError("Edit failed")
        })
        .finally(() => this.showLoading(false))
    },
    async deleteCredentialToApi(id) {
      this.showLoading(true)
      await deleteVeeamCredential(id, this.currentServerVeeam.id)
        .then(async (deleteCrendential) => {
          if (deleteCrendential.status === "ok") {
            this.toastSuccess("Delete successfully")
            await this.getDataFromApi()
          } else {
            this.toastError("Delete failed")
          }
        })
        .catch(() => {
          this.toastError("Delete failed")
        })
        .finally(() => this.showLoading(false))
    },
    async getServerCredentialListFromApi() {
      this.showLoading(true)
      this.serverCredentialList = []
      const baseFilter = {
        veeamId: this.currentServerVeeam.id,
      }
      await getVeeamServer(baseFilter)
        .then((response) => {
          if (response) {
            response.data.forEach((credential) => {
              this.serverCredentialList.push(credential.credentialsId)
            })
          }
        })
        .finally(() => this.showLoading(false))
    },
  },
}
</script>

<style lang="scss" scoped></style>
